// Row action menu + confirm dialog shared by every System Management table
// (Admin & Security and Registry Management) — the Registry Central pattern:
// "⋯" opens a menu anchored under the button with that table's existing
// actions, and anything that changes or removes a record asks to confirm.
import React from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { useAnchoredMenu } from '../../shared/useAnchoredMenu';

const TONES = {
  default: 'text-gray-700 hover:bg-gray-50',
  green: 'text-green-700 hover:bg-green-50',
  amber: 'text-amber-700 hover:bg-amber-50',
  red: 'text-red-600 hover:bg-red-50',
};

// items: [{ key, label, tone?: 'default'|'green'|'amber'|'red', dividerBefore?: boolean, hidden?: boolean }]
export default function SystemRowMenu({ rect, items, onPick, onClose }) {
  const { menuRef, style } = useAnchoredMenu(rect, onClose);
  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      <div ref={menuRef} role="menu" style={{ ...style, minWidth: 190 }} className="rounded-xl border border-gray-100 bg-white py-1.5 shadow-2xl">
        {items.filter((item) => !item.hidden).map((item) => (
          <React.Fragment key={item.key}>
            {item.dividerBefore && <div className="mx-3 my-1 h-px bg-gray-100" />}
            <button
              type="button"
              onClick={() => { onClose(); onPick(item.key); }}
              className={`w-full px-4 py-2.5 text-left text-sm font-medium ${TONES[item.tone] ?? TONES.default}`}
            >
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>
    </>,
    document.body
  );
}

// The standard View / Edit / (Activate|Deactivate) / Delete menu.
export function standardRowItems({ canEdit = true, canDelete = true, isActive = null } = {}) {
  return [
    { key: 'view', label: 'View Details' },
    { key: 'edit', label: 'Edit', hidden: !canEdit },
    { key: 'toggle-status', label: isActive ? 'Deactivate' : 'Activate', tone: isActive ? 'amber' : 'green', dividerBefore: true, hidden: isActive === null },
    { key: 'delete', label: 'Delete', tone: 'red', dividerBefore: isActive === null, hidden: !canDelete },
  ];
}

function ConfirmDialog({ title, message, confirmLabel, danger, onDone, zoom }) {
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 px-4" style={zoom !== 1 ? { zoom } : undefined}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="mb-2 text-base font-bold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={() => onDone(false)} className="h-10 flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="button" autoFocus onClick={() => onDone(true)}
            className={`h-10 flex-1 rounded-full text-sm font-semibold text-white ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Promise-based replacement for window.confirm in the System Management
// tables: `if (!(await confirmAction({ title, message }))) return;`
export function confirmAction({ title = 'Are you sure?', message = '', confirmLabel = 'Confirm', danger = false } = {}) {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    // Match the app shell's zoom so the dialog is the same size as the page.
    const shell = document.querySelector('.app-shell-zoom-75');
    const zoom = shell ? (parseFloat(window.getComputedStyle(shell).zoom) || 1) : 1;
    const done = (result) => {
      root.unmount();
      host.remove();
      resolve(result);
    };
    root.render(<ConfirmDialog title={title} message={message} confirmLabel={confirmLabel} danger={danger} onDone={done} zoom={zoom} />);
  });
}
